import React from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import { FiX, FiSend } from "react-icons/fi";
import { useAuthStore } from "@features/auth/store/authStore";
import { getRequestReviewCopy } from "@core/reports/utils/reportWorkflow";

export type RequestReviewTarget = "department" | "activity";

export type SourceTemplateOption = {
  template_id: number;
  template_name: string;
  submitted_by?: number | string | null;
};

export type RequestReviewConfirmPayload = {
  reason: string;
  target?: RequestReviewTarget;
  templateId?: number | null;
};

export type RequestReviewTargetOptions = {
  /** Allow returning the department report for corrections. */
  allowDepartment: boolean;
  /** Allow returning an activity submission for a source template. */
  allowActivity: boolean;
  /** Templates that compose this leadership report (from meta.source_templates). */
  sourceTemplates?: SourceTemplateOption[];
  /** Label for the department-report radio, e.g. "Pastor's department report". */
  departmentLabel: string;
};

export interface RequestReviewModalProps {
  show: boolean;
  onHide: () => void;
  /** Modal heading. Defaults to Request Edit or Request Review from submittedBy. */
  title?: string;
  /** Optional subject line, e.g. "You are requesting review for: <name>". */
  subject?: React.ReactNode;
  /** Last submitter user uid; drives edit vs review copy when title is omitted. */
  submittedBy?: number | string | null;
  busy: boolean;
  error?: string | null;
  /**
   * When set, the modal shows target (and optional template) radios before the reason.
   * Confirm then includes `target` / `templateId` on the payload.
   */
  targetOptions?: RequestReviewTargetOptions;
  /** Called with the trimmed reason (and optional target/template) once validation passes. */
  onConfirm: (payload: RequestReviewConfirmPayload) => void | Promise<void>;
}

/**
 * Shared "Request Review" modal: a reason textarea (min 10 chars, 1000 max) with
 * validation + confirm/cancel. Used by the church (per-template), district (per-church),
 * and nation (aggregate) report flows. Optionally supports choosing department vs activity
 * target (and which source template) for leadership church pages.
 */
export default function RequestReviewModal({
  show,
  onHide,
  title,
  subject,
  submittedBy,
  busy,
  error,
  targetOptions,
  onConfirm,
}: RequestReviewModalProps) {
  const currentUserId = useAuthStore((s) => s.getAuthUser()?.id ?? null);
  const copy = getRequestReviewCopy(currentUserId, submittedBy);
  const heading = title ?? copy.actionLabel;
  const [reason, setReason] = React.useState("");
  const [touched, setTouched] = React.useState(false);
  const [target, setTarget] = React.useState<RequestReviewTarget>("department");
  const [templateId, setTemplateId] = React.useState<number | null>(null);

  const reasonTrimmed = reason.trim();
  const reasonValid = reasonTrimmed.length >= 10;

  const templates = React.useMemo(
    () =>
      (targetOptions?.sourceTemplates ?? []).filter(
        (t) => Number(t.template_id) > 0 && String(t.template_name ?? "").trim() !== ""
      ),
    [targetOptions?.sourceTemplates]
  );

  const showTargetPicker =
    !!targetOptions && targetOptions.allowDepartment && targetOptions.allowActivity;

  const showTemplatePicker =
    !!targetOptions && target === "activity" && templates.length > 1;

  const selectedTemplate =
    templates.find((t) => t.template_id === templateId) ??
    (templates.length === 1 ? templates[0] : null);

  const needsTemplate =
    !!targetOptions &&
    ((showTargetPicker && target === "activity") ||
      (!targetOptions.allowDepartment && targetOptions.allowActivity));

  const templateValid = !needsTemplate || (selectedTemplate != null && selectedTemplate.template_id > 0);

  // Reset fields whenever the modal is (re)opened.
  React.useEffect(() => {
    if (!show) return;
    setReason("");
    setTouched(false);

    if (!targetOptions) {
      setTarget("department");
      setTemplateId(null);
      return;
    }

    setTarget(targetOptions.allowDepartment ? "department" : "activity");
    setTemplateId(templates[0]?.template_id ?? null);
  }, [show, targetOptions, templates]);

  const handleConfirm = async () => {
    setTouched(true);
    if (!reasonValid || !templateValid) return;

    if (!targetOptions) {
      await onConfirm({ reason: reasonTrimmed });
      return;
    }

    const resolvedTarget: RequestReviewTarget =
      targetOptions.allowDepartment && targetOptions.allowActivity
        ? target
        : targetOptions.allowDepartment
          ? "department"
          : "activity";

    await onConfirm({
      reason: reasonTrimmed,
      target: resolvedTarget,
      templateId:
        resolvedTarget === "activity"
          ? selectedTemplate?.template_id ?? templateId
          : null,
    });
  };

  const resolvedSubject =
    targetOptions && needsTemplate && selectedTemplate ? (
      <>
        You are requesting {copy.noun} for activity submission:{" "}
        <span className="fw-semibold">{selectedTemplate.template_name}</span>
      </>
    ) : (
      subject
    );

  return (
    <Modal show={show} onHide={() => (busy ? undefined : onHide())} centered backdrop="static">
      <Modal.Header closeButton={!busy}>
        <Modal.Title>{heading}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {resolvedSubject ? <p className="text-muted mb-2">{resolvedSubject}</p> : null}

        {showTargetPicker ? (
          <Form.Group className="mb-3">
            <Form.Label className="fw-medium">
              What are you returning for correction? <span className="text-danger">*</span>
            </Form.Label>
            <div>
              <Form.Check
                type="radio"
                id="request-review-target-department"
                name="request-review-target"
                label={targetOptions!.departmentLabel}
                checked={target === "department"}
                disabled={busy}
                onChange={() => setTarget("department")}
                className="mb-1"
              />
              <Form.Check
                type="radio"
                id="request-review-target-activity"
                name="request-review-target"
                label="An activity submission"
                checked={target === "activity"}
                disabled={busy}
                onChange={() => setTarget("activity")}
              />
            </div>
          </Form.Group>
        ) : null}

        {showTemplatePicker ? (
          <Form.Group className="mb-3">
            <Form.Label className="fw-medium">
              Activity submission template <span className="text-danger">*</span>
            </Form.Label>
            <div>
              {templates.map((t) => (
                <Form.Check
                  key={t.template_id}
                  type="radio"
                  id={`request-review-template-${t.template_id}`}
                  name="request-review-template"
                  label={t.template_name}
                  checked={templateId === t.template_id}
                  disabled={busy}
                  onChange={() => setTemplateId(t.template_id)}
                  className="mb-1"
                />
              ))}
            </div>
            {touched && !templateValid ? (
              <Form.Text className="text-danger">Please select a template.</Form.Text>
            ) : null}
          </Form.Group>
        ) : null}

        <Form.Group>
          <Form.Label className="fw-medium">
            {copy.reasonLabel} <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={reason}
            maxLength={1000}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="Explain what needs to be corrected..."
            disabled={busy}
            isInvalid={touched && !reasonValid}
          />
          <div className="d-flex justify-content-between mt-1">
            <Form.Text muted>Minimum 10 characters.</Form.Text>
            <Form.Text muted>{reason.length} / 1000</Form.Text>
          </div>
          <Form.Control.Feedback type="invalid">
            Please provide a reason (at least 10 characters).
          </Form.Control.Feedback>
        </Form.Group>
        {error ? (
          <Alert variant="danger" className="mt-3 mb-0">
            {error}
          </Alert>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>
          <FiX className="me-2" />
          Cancel
        </Button>
        <Button variant="primary" disabled={busy} onClick={handleConfirm}>
          {busy ? <Spinner animation="border" size="sm" className="me-2" /> : <FiSend className="me-2" />}
          {copy.confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
